'use client';

import './commitment-lists.scss';

import { useQueryState } from 'nuqs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import { type GraphQLPledge } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import {
  hasSupporterDataIssues,
  useSupporterDataStatus,
} from '@sola/supporter-sdk/hooks/data-status.ts';
import { commitmentsQuery, pledgesQuery } from '@sola/supporter-sdk/queries.ts';
import {
  filterCurrentCommitments,
  groupCommitmentsByFrequency,
  sortCommitmentsByPriority,
} from '@sola/supporter-sdk/utils/commitments.ts';

import { CommitmentCard } from '../../molecules/commitment-card/commitment-card.tsx';
import { PledgeCard } from '../../molecules/pledge-card/pledge-card.tsx';
import { PrimerError } from '../../molecules/primer-error/primer-error.tsx';
import { SectionContent } from '../../molecules/section-content/section-content.tsx';
import { usePreloadNamespaces } from '../../utils/use-preload-namespaces.ts';
import { PaymentIssueAlert } from '../payment-issue-alert/payment-issue-alert.tsx';
import {
  type CommitmentCardData,
  getCommitmentTotal,
} from './commitment-lists.logic.ts';

const getCardElementId = (id: string) => `commitment-card-${id}`;

/**
 * Get component data
 */
export const useComponentData = () => {
  const { t } = useTranslation('my');
  const { data, isLoading, isFetching, isPending, isError } = useSupporterQuery(
    [commitmentsQuery, pledgesQuery],
  );
  const { data: dataStatus } = useSupporterDataStatus();
  let commitmentFrequencyGroups: CommitmentCardData[] = [];

  const [commitments = [], pledges = []] = data;
  const hasDataIssue = hasSupporterDataIssues(dataStatus, [
    'commitments',
    'pledges',
  ]);

  const hasError = isError || hasDataIssue;

  if (commitments?.length) {
    const activeCommitments = filterCurrentCommitments(commitments);
    const {
      [CommitmentFrequencyEnum.Monthly]: monthlyCommitments,
      [CommitmentFrequencyEnum.Quarterly]: quarterlyCommitments,
      [CommitmentFrequencyEnum.Semiannually]: semiannualCommitments,
      [CommitmentFrequencyEnum.Annually]: annualCommitments,
    } = groupCommitmentsByFrequency(activeCommitments);

    commitmentFrequencyGroups = [
      {
        heading: t('commitments.headings.monthly'),
        frequencyAbbr: t('core:frequencies.abbreviated.monthly'),
        commitments: monthlyCommitments,
      },
      {
        heading: t('commitments.headings.quarterly'),
        frequencyAbbr: t('core:frequencies.abbreviated.quarterly'),
        commitments: quarterlyCommitments,
      },
      {
        heading: t('commitments.headings.semiannually'),
        frequencyAbbr: t('core:frequencies.abbreviated.semiannually'),
        commitments: semiannualCommitments,
      },
      {
        heading: t('commitments.headings.annually'),
        frequencyAbbr: t('core:frequencies.abbreviated.annually'),
        commitments: annualCommitments,
      },
    ];
  }

  const isShowingSkeletons =
    (isLoading || isFetching || isPending) && !hasError;

  return {
    isShowingSkeletons,
    commitmentFrequencyGroups,
    hasError,
    pledges,
  };
};

// TODO: Might be worthwhile to extract PledgeList from this component - which would allow for separating it from the Commitment List
type PledgeListProps = {
  pledges: GraphQLPledge[];
};

const PledgeList: React.FC<PledgeListProps> = ({ pledges }) => {
  const { t } = useTranslation(['my']);

  const [activePledgeId, setPledgeIdQuery] = useQueryState('id', {
    history: 'push',
  });
  const setActiveQueryId = (open: boolean, pledgeId: string) => {
    if (open) {
      setPledgeIdQuery(pledgeId);
    } else {
      setPledgeIdQuery(null);
    }
  };

  /**
   * Scroll to active card after page loads.
   */
  useEffect(() => {
    if (!activePledgeId) {
      return;
    }

    const activeCard = document.getElementById(
      getCardElementId(activePledgeId),
    );
    if (!activeCard) {
      return;
    }

    activeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return (
    <div key="commitment-group-pledge" className="CommitmentLists__group">
      <SectionContent heading={t('financialPledges')}>
        {pledges.map((pledge) => {
          if (pledge.pledgeId === null) {
            return null;
          }

          const id = getCardElementId(pledge.pledgeId!);

          return (
            <div id={id} key={id}>
              <PledgeCard
                pledge={pledge}
                onActiveChange={setActiveQueryId}
                isActive={pledge.pledgeId === activePledgeId}
              />
            </div>
          );
        })}
      </SectionContent>
    </div>
  );
};

export type CommitmentListGroupsProps = {
  commitmentFrequencyGroups: CommitmentCardData[];
};

const CommitmentListGroups: React.FC<CommitmentListGroupsProps> = ({
  commitmentFrequencyGroups,
}) => {
  const { t } = useTranslation('core');
  const [activeCommitmentId, setCommitmentIdQuery] = useQueryState('id', {
    history: 'push',
  });
  const setActiveQueryId = (open: boolean, commitmentId: string) => {
    if (open) {
      setCommitmentIdQuery(commitmentId);
    } else {
      setCommitmentIdQuery(null);
    }
  };

  /**
   * Scroll to active card after page loads.
   */
  useEffect(() => {
    if (!activeCommitmentId) {
      return;
    }

    const activeCard = document.getElementById(
      getCardElementId(activeCommitmentId),
    );
    if (!activeCard) {
      return;
    }

    activeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return commitmentFrequencyGroups.map(
    ({ heading, frequencyAbbr, commitments: commitmentsByFrequency }) => {
      if (!commitmentsByFrequency?.length) {
        return null;
      }

      const commitmentsByType = sortCommitmentsByPriority(
        commitmentsByFrequency,
        t,
      );

      return (
        !!commitmentsByType?.length && (
          <div
            className="CommitmentLists__group"
            key={`commitment-group-${heading}`}
          >
            <SectionContent
              heading={heading}
              subheading={
                <>
                  {t('numbers.currencyCustom', {
                    amount: getCommitmentTotal(commitmentsByFrequency),
                  })}
                  <span className="label-neutral-mild">{frequencyAbbr}</span>
                </>
              }
            >
              {commitmentsByType.map((commitment) => {
                const id = getCardElementId(commitment.internalId);
                return (
                  <div id={id} key={id}>
                    <CommitmentCard
                      commitment={commitment}
                      onActiveChange={setActiveQueryId}
                      isActive={commitment.internalId === activeCommitmentId}
                    />
                  </div>
                );
              })}
            </SectionContent>
          </div>
        )
      );
    },
  );
};

const CommitmentListsEmptyState: React.FC = () => {
  const { t } = useTranslation(['my', 'core']);

  return (
    <>
      <h3 className="heading-sm mb-snug-xl">
        {t('my:commitments.noCommitments.title')}
      </h3>

      <p className="body-sm label-neutral-mild">
        {t('my:commitments.noCommitments.message')}
      </p>

      <p className="flex">
        <ButtonStyle className="" size="md" variant="primary">
          {/* TODO: Should use content position or some other means of getting SAC url */}
          <a
            href={`${process.env.NEXT_PUBLIC_BASE_URL_DOTCOM}/sponsor-a-child/`}
          >
            {t('my:commitments.noCommitments.ctaText')}
          </a>
        </ButtonStyle>
      </p>
    </>
  );
};

const CommitmentListsContent: React.FC = () => {
  const { isShowingSkeletons, commitmentFrequencyGroups, hasError, pledges } =
    useComponentData();
  const { t } = useTranslation(['my']);

  // Warm the payment-methods namespace: from a commitment's details sheet a
  // supporter can open the change/add payment method flow, which is the only
  // place that namespace is used. Preloading here avoids a mid-interaction
  // suspend when they get there.
  usePreloadNamespaces('payment-methods');

  if (isShowingSkeletons) {
    return <SectionContent isLoadingContent isLoadingHeading />;
  }

  const hasPledges = !!pledges?.length;
  const hasCommitments = !!commitmentFrequencyGroups?.length;

  if (hasError && !hasCommitments && !hasPledges) {
    return (
      <Alert
        variant="negative"
        title={t('commitments.queryError.title')}
        message={t('commitments.queryError.message')}
        action={
          <a href={t('my:ctas.contactSupport.url')}>
            {t('my:ctas.contactSupport.text')}
          </a>
        }
      />
    );
  }

  return (
    <>
      <PrimerError />
      <PaymentIssueAlert />

      {hasPledges && <PledgeList pledges={pledges} />}

      {hasCommitments && (
        <CommitmentListGroups
          commitmentFrequencyGroups={commitmentFrequencyGroups}
        />
      )}

      {!hasPledges && !hasCommitments && <CommitmentListsEmptyState />}
    </>
  );
};

/**
 * Displays list of commitments based on frequency.
 */
export const CommitmentLists = () => {
  return (
    <div className="CommitmentLists max-w-md flex flex-col gap-comfy-xl">
      <CommitmentListsContent />
    </div>
  );
};
